import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { Invite, Item, ListDetail, ListSummary } from './types';

export const qk = {
  lists: ['lists'] as const,
  list: (id: string) => ['lists', id] as const,
  invites: (id: string) => ['lists', id, 'invites'] as const,
  invite: (token: string) => ['invite', token] as const,
};

// ---------------------------------------------------------------- queries

export function useLists() {
  return useQuery({ queryKey: qk.lists, queryFn: () => api.lists() });
}

export function useList(id: string) {
  return useQuery({
    queryKey: qk.list(id),
    queryFn: () => api.list(id),
    // 404 is rendered inline by the screen.
    meta: { silent: true },
  });
}

export function useInvites(listId: string, enabled: boolean) {
  return useQuery({
    queryKey: qk.invites(listId),
    queryFn: () => api.invites(listId),
    enabled,
  });
}

export function useInvitePreview(token: string) {
  return useQuery({
    queryKey: qk.invite(token),
    queryFn: () => api.invitePreview(token),
    meta: { silent: true },
    retry: false,
  });
}

// ---------------------------------------------------------------- helpers

function summaryCounts(items: Item[]) {
  return { itemCount: items.length, doneCount: items.filter((i) => i.done).length };
}

/** Apply a change to a cached list detail and keep the cached summary counts in sync. */
function patchList(qc: QueryClient, listId: string, fn: (items: Item[]) => Item[]) {
  qc.setQueryData<ListDetail>(qk.list(listId), (old) => {
    if (!old) return old;
    const items = fn(old.items);
    return { ...old, items, ...summaryCounts(items) };
  });
  const detail = qc.getQueryData<ListDetail>(qk.list(listId));
  if (detail) {
    qc.setQueryData<ListSummary[]>(qk.lists, (old) =>
      old?.map((l) => (l.id === listId ? { ...l, ...summaryCounts(detail.items) } : l)),
    );
  }
}

/** Snapshot list detail + summaries for rollback. */
async function snapshot(qc: QueryClient, listId: string) {
  await qc.cancelQueries({ queryKey: qk.list(listId), exact: true });
  return {
    detail: qc.getQueryData<ListDetail>(qk.list(listId)),
    lists: qc.getQueryData<ListSummary[]>(qk.lists),
  };
}

type Snap = Awaited<ReturnType<typeof snapshot>>;

function rollback(qc: QueryClient, listId: string, snap: Snap | undefined) {
  if (!snap) return;
  qc.setQueryData(qk.list(listId), snap.detail);
  qc.setQueryData(qk.lists, snap.lists);
}

/** Refetch once the last in-flight item mutation for this list settles (avoids flicker). */
function settle(qc: QueryClient, listId: string) {
  if (qc.isMutating({ mutationKey: ['items', listId] }) > 1) return;
  void qc.invalidateQueries({ queryKey: qk.list(listId), exact: true });
  void qc.invalidateQueries({ queryKey: qk.lists, exact: true });
}

let tmpSeq = 0;
export const isTempId = (id: string) => id.startsWith('tmp-');

// ---------------------------------------------------------------- lists

export function useCreateList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (title: string) => api.createList({ title }),
    onSuccess: (created) => {
      qc.setQueryData<ListSummary[]>(qk.lists, (old) => [created, ...(old ?? []).filter((l) => l.id !== created.id)]);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: qk.lists, exact: true }),
  });
}

export function useRenameList(listId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (title: string) => api.updateList(listId, { title }),
    onSuccess: (updated) => {
      qc.setQueryData<ListDetail>(qk.list(listId), (old) => (old ? { ...old, title: updated.title } : old));
      qc.setQueryData<ListSummary[]>(qk.lists, (old) =>
        old?.map((l) => (l.id === listId ? { ...l, title: updated.title } : l)),
      );
    },
    onSettled: () => qc.invalidateQueries({ queryKey: qk.lists }),
  });
}

function dropList(qc: QueryClient, listId: string) {
  qc.setQueryData<ListSummary[]>(qk.lists, (old) => old?.filter((l) => l.id !== listId));
  qc.removeQueries({ queryKey: qk.list(listId) });
}

export function useDeleteList(listId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteList(listId),
    onSuccess: () => dropList(qc, listId),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.lists, exact: true }),
  });
}

export function useLeaveList(listId: string, userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!userId) throw new Error('Not signed in.');
      return api.removeMember(listId, userId);
    },
    onSuccess: () => dropList(qc, listId),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.lists, exact: true }),
  });
}

export function useRemoveMember(listId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.removeMember(listId, userId),
    onMutate: async (userId) => {
      const snap = await snapshot(qc, listId);
      qc.setQueryData<ListDetail>(qk.list(listId), (old) =>
        old ? { ...old, members: old.members.filter((m) => m.id !== userId) } : old,
      );
      return snap;
    },
    onError: (_e, _v, snap) => rollback(qc, listId, snap),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.list(listId), exact: true }),
  });
}

// ---------------------------------------------------------------- items (optimistic)

export function useAddItem(listId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['items', listId],
    mutationFn: ({ text }: { text: string; tempId: string }) => api.addItem(listId, text),
    onMutate: async ({ text, tempId }) => {
      const snap = await snapshot(qc, listId);
      const now = new Date().toISOString();
      const temp: Item = { id: tempId, text, done: false, listId, createdAt: now, updatedAt: now };
      patchList(qc, listId, (items) => [...items, temp]);
      return snap;
    },
    onSuccess: (item, { tempId }) => {
      patchList(qc, listId, (items) => items.map((i) => (i.id === tempId ? item : i)));
    },
    onError: (_e, _v, snap) => rollback(qc, listId, snap),
    onSettled: () => settle(qc, listId),
  });
}

export function newTempId() {
  tmpSeq += 1;
  return `tmp-${Date.now()}-${tmpSeq}`;
}

export function useToggleItem(listId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['items', listId],
    mutationFn: ({ itemId, done }: { itemId: string; done: boolean }) => api.updateItem(listId, itemId, { done }),
    onMutate: async ({ itemId, done }) => {
      const snap = await snapshot(qc, listId);
      patchList(qc, listId, (items) => items.map((i) => (i.id === itemId ? { ...i, done } : i)));
      return snap;
    },
    onError: (_e, _v, snap) => rollback(qc, listId, snap),
    onSettled: () => settle(qc, listId),
  });
}

export function useDeleteItem(listId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['items', listId],
    mutationFn: (itemId: string) => api.deleteItem(listId, itemId),
    onMutate: async (itemId) => {
      const snap = await snapshot(qc, listId);
      patchList(qc, listId, (items) => items.filter((i) => i.id !== itemId));
      return snap;
    },
    onError: (_e, _v, snap) => rollback(qc, listId, snap),
    onSettled: () => settle(qc, listId),
  });
}

// ---------------------------------------------------------------- invites

export function useCreateInvite(listId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.createInvite(listId, {}),
    onSuccess: (inv) => {
      qc.setQueryData<Invite[]>(qk.invites(listId), (old) => [inv, ...(old ?? []).filter((i) => i.id !== inv.id)]);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: qk.invites(listId) }),
  });
}

export function useRevokeInvite(listId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) => api.revokeInvite(listId, inviteId),
    onMutate: async (inviteId) => {
      await qc.cancelQueries({ queryKey: qk.invites(listId) });
      const prev = qc.getQueryData<Invite[]>(qk.invites(listId));
      qc.setQueryData<Invite[]>(qk.invites(listId), (old) => old?.filter((i) => i.id !== inviteId));
      return prev;
    },
    onError: (_e, _v, prev) => qc.setQueryData(qk.invites(listId), prev),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.invites(listId) }),
  });
}

export function useAcceptInvite(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.acceptInvite(token),
    onSuccess: (list) => {
      qc.setQueryData<ListSummary[]>(qk.lists, (old) => (old ? [list, ...old.filter((l) => l.id !== list.id)] : old));
      void qc.invalidateQueries({ queryKey: qk.lists });
      void qc.invalidateQueries({ queryKey: qk.invite(token) });
    },
  });
}

export function isActiveInvite(i: Invite, now = Date.now()) {
  return !i.revoked && (!i.expiresAt || new Date(i.expiresAt).getTime() > now);
}
